import * as React from 'react'
import * as cn from 'classnames'
import * as styles from './Hover.module.scss'
import { WatchlistAction, DVString, TrailerFragment, WPSClient, withArgs, Args, ItemMaturityRating, StorefrontItem, EnrichmentRequest, StorefrontItemLive, ItemPrerolls, EnrichItemMetadataOutput } from 'DVWebNodeUtils'
import WatchlistStatus from './Hover/WatchlistStatus'
import { InfoboxProps, Infobox } from './Hover/Infobox'
import { Icon, IconType, Badge, badges, Hover as BasicHover } from 'DVWebNodeUI'
import { CommonPlaybackAction } from './Hover/Headline/PlaybackBlock'
import { ReviewsRating } from './Hover/ReviewsRating'
import { Tooltip } from './Hover/Tooltip'
import { Headline } from './Hover/Headline'
import PrerollPlayer, { PrerollPlayerPlacement } from '../DVWebNodeStorefront/shared/components/preroll/PrerollPlayer'
import { CustomerReviews } from '../../util/types/wps/hover'
import { VideoContext } from '../DVWebNodeStorefront/shared/utils/videoContext'

export const stringifyEnrichmentsOnClient = (enrichment?: EnrichmentRequest[] | undefined): EnrichmentRequest[] | string => (
    global.__SERVER__ === true ? enrichment : enrichment && JSON.stringify(enrichment)
)

const METRIC_PREFIX = 'DVWeb::Hover2019::'
const METRICS = {
    enrichAttempt: METRIC_PREFIX + 'Enrich::Attempt',
    enrichAttemptInitial: METRIC_PREFIX + 'Enrich::Attempt::Initial',
    enrichComplete: METRIC_PREFIX + 'Enrich::Complete',
    enrichCompleteInitial: METRIC_PREFIX + 'Enrich::Complete::Initial',
    hoverNoEnrich: METRIC_PREFIX + 'Hover::NoEnrich',
    hover: METRIC_PREFIX + 'Hover'
}

interface UpdateableHoverProps {
    title?: string
    titleID?: string
    titleIDType?: "EPISODE" | "SEASON" | "MOVIE" | "CHANNEL" | "LIVEEVENT" | "UNKNOWN"
    synopsis?: string
    hasSubtitles?: boolean
    watchlistAction?: WatchlistAction
    liveInfo?: StorefrontItemLive
    liveBadgeLabel?: string
    playbackAction?: CommonPlaybackAction
    maturityRating?: ItemMaturityRating
    imdbRating?: string
    releaseYear?: string | number
    runtime?: string
    trailer?: TrailerFragment
    offers?: DVString[]
    infoboxes?: InfoboxProps[]
    customerReviews?: CustomerReviews
    prerolls?: ItemPrerolls
    url?: string
    errored?: boolean
}

interface HoverProps extends UpdateableHoverProps {
    className?: string
    disabled?: boolean
    children: React.ReactChild | React.ReactChild[]
    placeholderClassName?: string
    enrichTrailer?: boolean
    showPrerollsInHover?: boolean
    onMouseOver?: (event: React.MouseEvent<HTMLDivElement>) => void
    onMouseLeave?: (event: React.MouseEvent<HTMLDivElement>) => void
    onWatchlistToggle?: (newAction: WatchlistAction) => void
}

declare let window: Window & {
    P: any
}

export interface PlayerCloseOpt {
    //This is incomplete, and declares only the parts needed for our current use case.
    videoType: string
    asin: string
    trailer: boolean
}

export interface RegulatoryRatingBadgeProps {
    displayText: string;
    description: string;
    countryCode?: string;
    simplifiedId?: string;
}


const TrailerWrapper = ({ trailer, children }: { trailer: TrailerFragment, children: any }) => (
    <Tooltip text={trailer.text}>
        <a
            className={'js-deeplinkable'}
            href={trailer.playbackURL}
            data-page-title-id={trailer.playbackID}
            data-video-type={trailer.videoMaterialType}
            data-use-full-view="true"
            data-is-trailer="true"
            data-title-id={trailer.playbackID}
            data-ux-click="true">
            {children}
        </a>
    </Tooltip>
)

const IconWrapper = ({ children }: { children: any }) => (
    <div className={cn(styles.iconWrapper, styles.alignBottom, styles.spaceRight)}>
        {children}
    </div>
)

const createPlaybackAction = (enrichment: StorefrontItem): CommonPlaybackAction => {
    if (!enrichment)
        return null

    if (enrichment && !enrichment.playbackAction)
        return null

    const newProgress = enrichment.progress && enrichment.progress.percentage
    const newPlaybackAction = enrichment.playbackAction

    const merged = {
        ...newPlaybackAction,
        progress: newProgress
    }

    return merged
}

const extractTitleIDTypeFromWatchlistAction = (watchlistAction: WatchlistAction) => {
    const valueInWatchlist = watchlistAction && watchlistAction.endpoint &&
        watchlistAction.endpoint.query && watchlistAction.endpoint.query.titleType
    if (valueInWatchlist) {
        const asLower = valueInWatchlist.toLowerCase()
        if (asLower == "season")
            return "SEASON"
        if (asLower == "event")
            return "LIVEEVENT"
    }
    return "UNKNOWN"
}

const extractTitleIdFromWatchlistAction = (watchlistAction: WatchlistAction) => {
    return watchlistAction &&
        watchlistAction.endpoint &&
        watchlistAction.endpoint.query &&
        watchlistAction.endpoint.query.titleID
}


class HoverComponent extends React.Component<HoverProps & Args, UpdateableHoverProps> {
    enrichmentInProgress: boolean
    initialEnrichmentComplete: boolean
    canEnrichTrailer: boolean
    prerollComponent: PrerollPlayer
    expanded: boolean

    constructor(props: HoverProps & Args) {
        super(props)
        this.state = {
            ...this.props,
            titleIDType: this.props.titleIDType || extractTitleIDTypeFromWatchlistAction(this.props.watchlistAction),
            titleID: this.props.titleID || extractTitleIdFromWatchlistAction(this.props.watchlistAction)
        }
        this.canEnrichTrailer = !Boolean(this.props.trailer)
    }

    isThisTitle(comparisonID: string) {
        const isScheduledId = this.state.titleID && this.state.titleID == comparisonID
        const isPlaybackActionId = this.state.playbackAction &&
            this.state.playbackAction.titleID &&
            this.state.playbackAction.titleID == comparisonID
        return isScheduledId || isPlaybackActionId
    }

    enrichMe(fetchTrailer: boolean, fetchPlayback: boolean, setEnrichedOnce?: boolean): Promise<void> {
        if (this.enrichmentInProgress)
            return Promise.resolve()
        this.enrichmentInProgress = true
        const titleID = this.state.titleID

        if (titleID && this.state.titleIDType != "CHANNEL") {
            const enrichmentRequest = this.createEnrichmentRequest(fetchTrailer, fetchPlayback)

            const wps = new WPSClient()
            this.props.logger.count(METRICS.enrichAttempt, 1, false)
            if (setEnrichedOnce)
                this.props.logger.count(METRICS.enrichAttemptInitial, 1, false)
            return wps.EnrichItemMetadata({
                context: this.props.context,
                itemsToEnrich: stringifyEnrichmentsOnClient([enrichmentRequest])
            }).then(response => {
                this.props.logger.count(METRICS.enrichComplete, 1, false)
                this.applyEnrichmentResponse(response, fetchPlayback)
                if (setEnrichedOnce) {
                    this.props.logger.count(METRICS.enrichCompleteInitial, 1, false)
                    this.initialEnrichmentComplete = true
                }
                if (fetchTrailer && this.canEnrichTrailer)
                    this.canEnrichTrailer = false
                this.enrichmentInProgress = false
            }
            ).catch(error => {
                this.props.logger.error(error)
                this.enrichmentInProgress = false
            })
        } else return Promise.resolve()
    }

    applyEnrichmentResponse(response: EnrichItemMetadataOutput, updatePlayback?: boolean) {
        const titleID = this.state.titleID
        if (response.enrichments && response.enrichments[titleID]) {

            const enrichment = response.enrichments[titleID]

            this.setState(
                {
                    trailer: enrichment.trailer || this.state.trailer,
                    playbackAction: updatePlayback ? createPlaybackAction(enrichment) : this.state.playbackAction,
                    offers: (enrichment.messages && enrichment.messages.offers && enrichment.messages.offers.length) ? enrichment.messages.offers : this.state.offers,
                    prerolls: enrichment.prerolls || this.state.prerolls
                }
            )
        }
    }

    createEnrichmentRequest(fetchTrailer: boolean, fetchPlayback: boolean): EnrichmentRequest {
        const shouldCallCcts = this.state.titleIDType &&
            (this.state.titleIDType == "SEASON" || this.state.titleIDType == "LIVEEVENT")
        this.enrichmentInProgress = true
        const titleID = this.state.titleID
        const isLive = Boolean(this.state.liveBadgeLabel || this.state.liveInfo)


        return {
            titleID: titleID,
            titlePlayable: !shouldCallCcts,
            trailer: fetchTrailer,
            playback: fetchPlayback,
            preroll: this.props.showPrerollsInHover && !Boolean(this.props.prerolls),
            ...(isLive && { live: isLive })
        }

    }

    initialEnrich(): Promise<void> {
        if (!this.initialEnrichmentComplete)
            return this.enrichMe(this.canEnrichTrailer, true, true)
        else {
            this.props.logger.count(METRICS.hoverNoEnrich, 1, false)
            return Promise.resolve()
        }
    }

    onHover(ev: React.MouseEvent<HTMLDivElement>) {
        this.expanded = true
        if (this.props.onMouseOver)
            this.props.onMouseOver(ev)
        this.props.logger.count(METRICS.hover, 1, false)

        this.initialEnrich().then(_response => {
            this.expanded && this.prerollComponent && this.prerollComponent.startPlayer()
        })
    }

    onLeave(ev: React.MouseEvent<HTMLDivElement>) {
        this.expanded = false
        if (this.props.onMouseLeave)
            this.props.onMouseLeave(ev)

        this.prerollComponent && this.prerollComponent.stop()
    }

    onFocus() {
        this.expanded = true
        this.initialEnrich()
    }

    onBlur() {
        this.expanded = false
    }

    componentDidMount() {
        if (window && window.P) {
            window.P.when('dv-player-integration').execute((player: any) => {
                player.on('play', (opt: PlayerCloseOpt) => {
                    const playingTitle = opt.asin
                    const isPlayingCurrentItem = this.isThisTitle(playingTitle)
                    if (isPlayingCurrentItem) {
                        const fetchTrailer = true
                        const fetchPlayback = true
                        player.on('closed', () => this.enrichMe(fetchTrailer, fetchPlayback)
                        )
                    }
                })
            }
            )
        }
    }

    componentDidCatch() {
        this.setState({ errored: true })
    }

    render() {
        if (this.state.errored || this.props.disabled)
            return this.props.children
        const watchlistAction = this.state.watchlistAction &&
            { ...this.state.watchlistAction, text: this.state.watchlistAction.text as DVString }

        const preFirstLine = (this.state.liveBadgeLabel || (this.state.liveInfo &&
            this.state.liveInfo.status == "LIVE" &&
            this.state.liveInfo.label)) &&
            <div className={styles.preFirstLine}>
                <Badge className={styles.tailContent}
                    use={badges.LIVE}>
                    {this.state.liveBadgeLabel || this.state.liveInfo.label}
                </Badge>
            </div>

        const mainactions = <>
            {preFirstLine}
            <div className={styles.firstLine}>
                <Headline playbackAction={this.state.playbackAction}
                    offers={this.state.offers}
                    title={this.state.title} />
                <div className={styles.additionalIcons}>
                    {this.state.trailer &&
                        <IconWrapper>
                            <TrailerWrapper
                                trailer={this.state.trailer} >
                                <Icon use={IconType.TRAILER} className={cn(styles.tailContent, styles.alignBottom)} />
                            </TrailerWrapper>
                        </IconWrapper>}
                    {watchlistAction &&
                        <IconWrapper>
                            <WatchlistStatus
                                onToggle={this.props.onWatchlistToggle}
                                watchlistAction={watchlistAction}
                                prerolls={this.props.prerolls}
                            />
                        </IconWrapper>}
                </div>
            </div>
        </>


        const boxes = this.state.infoboxes &&
            this.state.infoboxes.map(infobox => <Infobox {...infobox} />)

        const synopsis = <div className={styles.summary}>
            <h1 className={styles.title}>
                {this.props.title}
            </h1>
            <p className={styles.synopsis}>
                {this.state.synopsis}
            </p>
        </div>

        const extraMetadata = <>
            {this.state.liveInfo && this.state.liveInfo.timeBadge &&
                <div className={styles.extraMetadata}>
                    {this.state.liveInfo && this.state.liveInfo.timeBadge &&
                        <span className={cn(styles.tailContent, styles.tailText)}>
                            {this.state.liveInfo.timeBadge}
                        </span>}
                </div>}
            <div className={styles.extraMetadata}>
                {this.state.liveInfo && this.state.liveInfo.venue &&
                    <div className={cn(styles.tailContent, styles.tailText)}>
                        {this.state.liveInfo.venue}
                    </div>}
                {this.state.customerReviews &&
                    <div className={styles.tailContent}>
                        <ReviewsRating rating={this.state.customerReviews.value} />
                    </div>}
                {this.state.imdbRating &&
                    <div className={cn(styles.tailContent, styles.tailText)}>
                        IMDb {this.state.imdbRating}
                    </div>}
                {this.state.runtime &&
                    <div className={cn(styles.tailContent, styles.tailText)}>
                        {this.state.runtime}
                    </div>}
                {this.state.releaseYear &&
                    <div className={cn(styles.tailContent, styles.tailText)}>
                        {this.state.releaseYear}
                    </div>}
                {this.state.hasSubtitles &&
                    <div className={cn(styles.tailContent, styles.alignMiddle)} >
                        <Icon use={IconType.SUBTITLES} />
                    </div>}
                {this.state.maturityRating &&
                    <div className={cn(styles.tailContent)}>
                        <Badge use={badges.REGULATORY_RATING}>
                            {this.state.maturityRating.rating}
                        </Badge>
                    </div>}
            </div>
        </>

        const blocks = [mainactions, boxes, synopsis, extraMetadata].filter(line => Boolean(line))

        return (
            <BasicHover
                contentBlocks={blocks}
                className={this.props.className}
                placeholderClassName={this.props.placeholderClassName}
                onMouseLeave={(ev) => this.onLeave(ev)}
                onMouseOver={(ev) => this.onHover(ev)}
                onFocusCapture={() => this.onFocus()}
                onBlurCapture={() => this.onBlur()}>
                <>
                    {this.props.children}
                    {Boolean(this.state.prerolls) &&
                        <VideoContext.Consumer>
                            {(context) =>
                                <PrerollPlayer
                                    prerolls={this.state.prerolls}
                                    placement={PrerollPlayerPlacement.Packshot}
                                    link={this.props.url}
                                    videoContext={context}
                                    playerIsActive={true}
                                    parentListeners={{
                                        onPlayStart: () => {
                                            if (!this.expanded && this.prerollComponent) {
                                                this.prerollComponent.stop()
                                            } 
                                        }
                                    }}
                                    ref={(component) => this.prerollComponent = component}
                                />
                            }
                        </VideoContext.Consumer>
                    }
                </>
            </BasicHover>
        )
    }
}

export const Hover = withArgs(HoverComponent) as React.ComponentType<HoverProps>